import { useEffect, useRef, useState } from "react";
import { ArrowUp, Bot, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, streamCoach } from "@/lib/api";
import { Loader, PageHeader } from "@/components/common";
import { MiniMarkdown } from "@/components/MiniMarkdown";
import { AiPlanButton, AiPlanDialog } from "@/components/AiPlanDialog";

const PLAN_INTENT = /(adapta|cambia|ajusta|modifica|reorganiza|rehaz).{0,40}(semana|plan|entren|rutina|menú|menu|comidas|dieta)/i;

const SUGGESTIONS = [
  "¿Qué entreno me toca hoy y cómo lo hago bien?",
  "Dame una alternativa a la cena de hoy",
  "¿Cómo cuido mi dolencia esta semana?",
  "¿Voy bien con mi objetivo de peso?",
];

const Bubble = ({ m, i }) =>
  m.role === "user" ? (
    <div className="ml-auto max-w-[85%] rounded-3xl rounded-br-lg bg-ink px-4 py-3 text-sm text-white" data-testid={`coach-msg-user-${i}`}>{m.content}</div>
  ) : (
    <div className="max-w-[92%]" data-testid={`coach-msg-assistant-${i}`}>
      <div className="rounded-3xl rounded-bl-lg border border-black/5 bg-white px-4 py-3 text-ink shadow-sm">
        {m.content ? <MiniMarkdown text={m.content} /> : <span className="inline-flex gap-1" data-testid="coach-typing">{[0, 1, 2].map((d) => <span key={d} className="h-2 w-2 animate-bounce rounded-full bg-stone-300" style={{ animationDelay: `${d * 120}ms` }} />)}</span>}
      </div>
      {m.provider && <p className="mt-1 pl-2 text-[11px] text-stone-400" data-testid={`coach-provider-${i}`}>vía {m.provider}{m.skipped?.length ? ` · saltados: ${m.skipped.join(", ")}` : ""}</p>}
    </div>
  );

export default function Coach() {
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [planReq, setPlanReq] = useState("");
  const [planOpen, setPlanOpen] = useState(false);
  const end = useRef(null);
  useEffect(() => { api.get("/coach/messages").then((r) => setMsgs(r.data)); }, []);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  if (!msgs) return <Loader />;

  const patchLast = (fn) => setMsgs((xs) => [...xs.slice(0, -1), fn(xs[xs.length - 1])]);
  const send = async (q) => {
    const message = (q ?? text).trim();
    if (!message || busy) return;
    setText("");
    setBusy(true);
    if (PLAN_INTENT.test(message)) setPlanReq(message);
    setMsgs((xs) => [...xs, { role: "user", content: message }, { role: "assistant", content: "" }]);
    try {
      await streamCoach(message, (ev) => {
        if (ev.type === "provider") patchLast((m) => ({ ...m, provider: ev.name, skipped: ev.skipped }));
        if (ev.type === "delta") patchLast((m) => ({ ...m, content: m.content + ev.content }));
        if (ev.type === "error") patchLast((m) => ({ ...m, content: ev.content, provider: "sin conexión" }));
      });
    } catch (e) {
      patchLast((m) => ({ ...m, content: e.message }));
    } finally {
      setBusy(false);
    }
  };
  const clear = async () => { await api.delete("/coach/messages"); setMsgs([]); toast.success("Conversación borrada"); };

  return (
    <div className="flex min-h-[calc(100vh-9rem)] flex-col" data-testid="coach-page">
      <PageHeader eyebrow="Coach IA" title="Pregunta a Triactiva" subtitle="Conoce tu perfil, tu plan de hoy, tus comidas y tus dolencias.">
        <div className="mt-3"><AiPlanButton testid="coach-ai-plan-btn" /></div>
        {msgs.length > 0 && <button onClick={clear} data-testid="coach-clear-btn" className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-stone-500 hover:text-ink"><Trash2 className="h-3.5 w-3.5" /> Borrar conversación</button>}
      </PageHeader>
      <div className="flex-1 space-y-4" data-testid="coach-messages">
        {!msgs.length && (
          <div className="card-clay p-6">
            <div className="flex items-center gap-3"><span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-ink text-white"><Bot className="h-5 w-5" /></span><p className="font-display font-bold">¿En qué te ayudo hoy?</p></div>
            <div className="mt-5 flex flex-wrap gap-2">
              {SUGGESTIONS.map((s, i) => <button key={s} onClick={() => send(s)} data-testid={`coach-suggestion-${i}`} className="rounded-full border border-stone-200 bg-white px-4 py-2 text-left text-sm transition-colors hover:border-ink">{s}</button>)}
            </div>
          </div>
        )}
        {msgs.map((m, i) => <Bubble key={i} m={m} i={i} />)}
        {planReq && !busy && (
          <div className="card-clay flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="coach-plan-cta">
            <p className="text-sm font-semibold">¿Quieres que aplique esto a tu plan de la semana?</p>
            <button onClick={() => setPlanOpen(true)} data-testid="coach-plan-cta-btn" className="rounded-full bg-ink px-4 py-2 text-xs font-semibold text-white">Ver propuesta de cambios</button>
          </div>
        )}
        <AiPlanDialog open={planOpen} onOpenChange={(o) => { setPlanOpen(o); if (!o) setPlanReq(""); }} initialRequest={planReq} />
        <div ref={end} />
      </div>
      <p className="mt-4 text-center text-[11px] text-stone-400">Orientación general: no sustituye a un profesional sanitario.</p>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="sticky bottom-24 mt-2 flex items-end gap-2 rounded-3xl border border-black/10 bg-white p-2 shadow-lg lg:bottom-6">
        <textarea rows={1} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Escribe tu duda…" className="max-h-32 flex-1 resize-none bg-transparent px-3 py-2.5 text-sm outline-none" data-testid="coach-input" />
        <button disabled={busy || !text.trim()} data-testid="coach-send-btn" className="flex h-11 w-11 items-center justify-center rounded-full bg-ink text-white transition-transform active:scale-90 disabled:opacity-40"><ArrowUp className="h-5 w-5" /></button>
      </form>
    </div>
  );
}
